import { useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { getTaskService } from '@src/container';

/**
 * Hook: useDeleteTask
 * Mutation hook for deleting a task
 * 
 * @param options - React Query mutation options
 * @returns Mutation result
 * 
 * @example
 * ```typescript
 * const { mutate: deleteTask, isPending } = useDeleteTask({
 *   onSuccess: () => {
 *     toast.success('Task deleted successfully');
 *   }
 * });
 * 
 * deleteTask('task-123');
 * ```
 */
export function useDeleteTask(
    options?: Omit<UseMutationOptions<void, Error, string>, 'mutationFn'>
) {
    const taskService = getTaskService();
    const queryClient = useQueryClient();

    // Extract user's onSuccess before creating mutation
    const { onSuccess: userOnSuccess, ...restOptions } = options || {};

    return useMutation({
        ...restOptions,
        mutationFn: async (taskId: string) => {
            return taskService.deleteTask.execute(taskId);
        },
        onSuccess: async (data, variables, context, extra) => {
            // Invalidate task queries
            await queryClient.invalidateQueries({ queryKey: ['tasks'] });
            queryClient.removeQueries({ queryKey: ['tasks', variables] });

            // Call user's onSuccess callback if provided
            if (userOnSuccess) {
                await userOnSuccess(data, variables, context, extra);
            }
        },
    });
}
